import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { fetchEvents } from "../../ApiService/a-api.service";
import { Types } from "../../../types/types";
import MovieCard from "../MovieCard/MovieCard";
import longArrowRight from "../../../icons/longArrowRight.svg";
import styles from "./MovieList.module.scss";
import { useSearchParams, useLocation } from "react-router-dom";
import { useIsMobile } from "../../../hooks/useIsMobile";
import CalendarIcon from "../../../../src/icons/Calendar.svg";
import ChevronDownIcon from "../../../../src/icons/ChevronDown.svg";

const categories = [
  "Все",
  "Музыка",
  "Образование",
  "Культура",
  "Здоровье",
  "Отдых",
  "IT",
];

const MovieList: React.FC = () => {
  const [events, setEvents] = useState<Types[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Types[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeCategory, setActiveCategory] = useState<string>("Все");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const isMobile = useIsMobile();
  const limit = initialLoad && !isMobile ? 6 : filteredEvents.length;

  const sortOrder = "asc";
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const urlCategory = searchParams.get("category") || location.state?.category;

  const dateInputRef = useRef<HTMLInputElement | null>(null);

  const months = [
    "Января",
    "Февраля",
    "Марта",
    "Апреля",
    "Мая",
    "Июня",
    "Июля",
    "Августа",
    "Сентября",
    "Октября",
    "Ноября",
    "Декабря",
  ];

  const handleDateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Enter",
      "Escape",
      "Backspace",
      "Delete",
    ];

    if (allowedKeys.includes(e.key)) return;

    // Любую "печатаемую" клавишу блокируем
    if (e.key.length === 1) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchEvents();
        const sortedData = sortByDate(data, sortOrder);
        setEvents(sortedData);
        setFilteredEvents(sortedData);
      } catch (error) {
        console.error("Ошибка загрузки событий:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const sortByDate = (data: Types[], order: string) => {
    return [...data].sort((a, b) => {
      const dateA = new Date(a.date_start).getTime();
      const dateB = new Date(b.date_start).getTime();
      return order === "asc" ? dateA - dateB : dateB - dateA;
    });
  };

  const handleCategoryChange = useCallback(
    (category: string) => {
      setActiveCategory(category);
      let filtered =
        category === "Все"
          ? events
          : events.filter((event) =>
              event.category.some((cat) => cat.name === category)
            );
      if (selectedDate) {
        filtered = filtered.filter((event) =>
          event.date_start.startsWith(selectedDate)
        );
      }
      setFilteredEvents(sortByDate(filtered, sortOrder));
    },
    [events, selectedDate, sortOrder]
  );

  useEffect(() => {
    if (urlCategory && categories.includes(urlCategory)) {
      handleCategoryChange(urlCategory);
    }
  }, [urlCategory, handleCategoryChange]);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);

    let filtered =
      activeCategory === "Все"
        ? events
        : events.filter((e) =>
            e.category.some((cat) => cat.name === activeCategory)
          );

    if (newDate) {
      filtered = filtered.filter((e) => e.date_start.startsWith(newDate));
    }

    setFilteredEvents(sortByDate(filtered, sortOrder));
  };

  if (loading) return <p>Загрузка событий...</p>;

  const formatDateForDisplay = (value: string) => {
    if (!value) return "Выберите дату";

    const parts = value.split("-");
    if (parts.length < 3) return "ДД.ММ.ГГГГ";

    const [year, rawMonth, rawDay] = parts;

    const monthNum = Number(rawMonth);
    const dayNum = Number(rawDay);

    if (
      !year ||
      Number.isNaN(monthNum) ||
      Number.isNaN(dayNum) ||
      monthNum < 1 ||
      monthNum > 12
    ) {
      return "ДД.ММ.ГГГГ";
    }

    const monthName = months[monthNum - 1];
    const day = String(dayNum).padStart(2, "0");

    return `${day} ${monthName} ${year}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.container_for_tittle}>
        <h1 className={styles.title}>Афиша событий</h1>
        {!isMobile && (
          <label
            className={styles.calendarWrapper}
            htmlFor="events-date"
            onClick={(e) => {
              e.preventDefault();

              if (!dateInputRef.current) return;
              const el = dateInputRef.current as any;

              if (typeof el.showPicker === "function") {
                el.showPicker();
              } else {
                dateInputRef.current.focus();
                dateInputRef.current.click();
              }
            }}
          >
            <img
              src={CalendarIcon}
              alt="Календарь"
              className={styles.calendarIcon}
            />
            <span className={styles.calendarText}>
              {formatDateForDisplay(selectedDate)}
            </span>
            <img
              src={ChevronDownIcon}
              alt="Открыть календарь"
              className={styles.calendarArrow}
            />
            <input
              ref={dateInputRef}
              id="events-date"
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              onKeyDown={handleDateKeyDown}
              className={styles.calendarInput}
            />
          </label>
        )}
      </div>

      <div className={styles.filters}>
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => {
              handleCategoryChange(category);
              setInitialLoad(true);
            }}
            className={`${styles.filterButton} ${
              activeCategory === category ? styles.active : ""
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {filteredEvents.length > 0 ? (
        <div className={styles.grid}>
          {filteredEvents.slice(0, limit).map((event) => (
            <Link
              to={`/event/${event.id}`}
              key={event.id}
              className={styles.cardLink}
            >
              <MovieCard event={event} />
            </Link>
          ))}
        </div>
      ) : (
        <p className={styles.emptyMessage}>Событий пока нет</p>
      )}

      {!isMobile && initialLoad && filteredEvents.length > 6 && (
        <div className={styles.seeMore}>
          <button
            className={styles.seeMoreBtn}
            onClick={() => setInitialLoad(false)}
          >
            <span>Смотреть больше</span>
            <img src={longArrowRight} alt="Стрелка вправо" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MovieList;
